/* AuthorsPanel.js
 *
 * Editable authors + affiliations control for the submission detail panel.
 * Mirrors the client-side Authors control but adapted for admin context:
 *   - No i18n dependency (plain labels)
 *   - HTML5 drag-and-drop (no external dnd library)
 *   - Supports multiple affiliations per author via checkboxes
 *   - Stores affiliations as 0-based integer arrays on each author
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import "./AuthorsPanel.css";

/* ---- helpers ---- */

function displayName(a) {
  return [a?.givenName, a?.familyName].filter(Boolean).join(" ") || "(unnamed)";
}

function affKey(aff) {
  return `${aff?.institution ?? ""}|${aff?.country ?? ""}`;
}

function rearrangeAffiliations(authorsList, affiliationsList) {
  const seen = new Set();
  const newAffs = [];

  for (const author of authorsList) {
    for (const idx of author.affiliations ?? []) {
      const aff = affiliationsList[idx];
      if (!aff) continue;
      const k = affKey(aff);
      if (!seen.has(k)) {
        seen.add(k);
        newAffs.push(aff);
      }
    }
  }

  const updatedAuthors = authorsList.map((author) => ({
    ...author,
    affiliations: (author.affiliations ?? [])
      .map((idx) => {
        const aff = affiliationsList[idx];
        if (!aff) return -1;
        return newAffs.findIndex((a) => affKey(a) === affKey(aff));
      })
      .filter((i) => i >= 0),
  }));

  return { authors: updatedAuthors, affiliations: newAffs };
}

const EMPTY_AUTHOR = { givenName: "", familyName: "", presenting: false, affiliations: [] };
const EMPTY_AFF = { institution: "", country: "" };

/* ---- component ---- */

export default function AuthorsPanel({
  authors: authorsProp,
  affiliations: affiliationsProp,
  onSave,
  readOnly = false,
}) {
  const [authors, setAuthors] = useState(authorsProp || []);
  const [affiliations, setAffiliations] = useState(affiliationsProp || []);

  // keep in sync if parent row changes
  useEffect(() => {
    setAuthors(authorsProp || []);
  }, [authorsProp]);
  useEffect(() => {
    setAffiliations(affiliationsProp || []);
  }, [affiliationsProp]);

  // dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [draft, setDraft] = useState(EMPTY_AUTHOR);
  const [newAff, setNewAff] = useState(EMPTY_AFF);
  const [showNewAff, setShowNewAff] = useState(false);

  // drag state
  const [dragOver, setDragOver] = useState(null);

  /* ---- save helpers ---- */

  const commit = useCallback((nextAuthors, nextAffiliations) => {
    setAuthors(nextAuthors);
    setAffiliations(nextAffiliations);
    onSave?.(nextAuthors, nextAffiliations);
  }, [onSave]);

  /* ---- dialog ---- */

  const openAdd = () => {
    setEditingIndex(null);
    setDraft({ ...EMPTY_AUTHOR });
    setNewAff(EMPTY_AFF);
    setShowNewAff(false);
    setDialogOpen(true);
  };

  const openEdit = (i) => {
    setEditingIndex(i);
    setDraft({ ...authors[i], affiliations: [...(authors[i].affiliations ?? [])] });
    setNewAff(EMPTY_AFF);
    setShowNewAff(false);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDraft(EMPTY_AUTHOR);
    setEditingIndex(null);
    setNewAff(EMPTY_AFF);
    setShowNewAff(false);
  };

  const saveDialog = () => {
    let nextAffs = [...affiliations];
    let finalDraft = { ...draft };

    if (showNewAff && newAff.institution.trim()) {
      const exists = nextAffs.findIndex((a) => affKey(a) === affKey(newAff));
      const idx = exists !== -1 ? exists : (nextAffs.push({ ...newAff }), nextAffs.length - 1);
      if (!finalDraft.affiliations.includes(idx)) {
        finalDraft.affiliations = [...finalDraft.affiliations, idx];
      }
    }

    const nextAuthors = editingIndex !== null
      ? authors.map((a, i) => (i === editingIndex ? finalDraft : a))
      : [...authors, finalDraft];

    const { authors: ra, affiliations: rf } = rearrangeAffiliations(nextAuthors, nextAffs);
    commit(ra, rf);
    closeDialog();
  };

  /* ---- delete ---- */

  const deleteAuthor = (i) => {
    const next = authors.filter((_, idx) => idx !== i);
    const { authors: ra, affiliations: rf } = rearrangeAffiliations(next, affiliations);
    commit(ra, rf);
  };

  const deleteAffiliation = (i) => {
    const nextAffs = affiliations.filter((_, idx) => idx !== i);
    const nextAuthors = authors.map((a) => ({
      ...a,
      affiliations: (a.affiliations ?? [])
        .filter((idx) => idx !== i)
        .map((idx) => (idx > i ? idx - 1 : idx)),
    }));
    const { authors: ra, affiliations: rf } = rearrangeAffiliations(nextAuthors, nextAffs);
    commit(ra, rf);
  };

  /* ---- drag to reorder authors ---- */

  const handleDragStart = (e, i) => {
    e.dataTransfer.setData("authorIdx", String(i));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, i) => {
    e.preventDefault();
    setDragOver(i);
  };

  const handleDrop = (e, toIdx) => {
    e.preventDefault();
    const fromIdx = parseInt(e.dataTransfer.getData("authorIdx"), 10);
    setDragOver(null);
    if (Number.isNaN(fromIdx) || fromIdx === toIdx) return;
    const next = [...authors];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    const { authors: ra, affiliations: rf } = rearrangeAffiliations(next, affiliations);
    commit(ra, rf);
  };

  const handleDragLeave = () => setDragOver(null);

  /* ---- render ---- */

  const toggleDraftAff = (idx) => {
    const cur = draft.affiliations ?? [];
    const next = cur.includes(idx) ? cur.filter((i) => i !== idx) : [...cur, idx];
    setDraft((d) => ({ ...d, affiliations: next }));
  };

  const canSave =
    draft.givenName?.trim() || draft.familyName?.trim();

  return (
    <div className="authors-panel">
      {/* Authors row */}
      <div className="authors-panel__section">
        <Typography className="authors-panel__label">Authors</Typography>
        <div
          className="authors-panel__chips"
          onDragLeave={handleDragLeave}
        >
          {authors.map((author, i) => {
            const refs = (author.affiliations ?? [])
              .map((idx) => idx + 1)
              .join(", ");
            const label = (
              <span className={author.presenting ? "authors-panel__chip-presenting" : undefined}>
                {displayName(author)}
                {refs ? <sup> {refs}</sup> : null}
              </span>
            );

            return (
              <div
                key={i}
                className={`authors-panel__chip-wrap${dragOver === i ? " is-drag-over" : ""}`}
                draggable={!readOnly}
                onDragStart={(e) => handleDragStart(e, i)}
                onDragOver={(e) => handleDragOver(e, i)}
                onDrop={(e) => handleDrop(e, i)}
              >
                {!readOnly && (
                  <span className="authors-panel__drag-handle" title="Drag to reorder">
                    <DragIndicatorIcon fontSize="small" />
                  </span>
                )}
                <Chip
                  label={label}
                  size="small"
                  style={{ borderRadius: 6 }}
                  onClick={readOnly ? undefined : () => openEdit(i)}
                  onDelete={readOnly ? undefined : () => deleteAuthor(i)}
                  deleteIcon={<CloseIcon />}
                />
                {!readOnly && (
                  <Tooltip title="Edit author" placement="top">
                    <IconButton size="small" onClick={() => openEdit(i)} className="authors-panel__edit-btn">
                      <EditRoundedIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            );
          })}

          {!readOnly && (
            <Chip
              icon={<AddIcon />}
              label="Add author"
              size="small"
              onClick={openAdd}
              className="authors-panel__add-chip"
            />
          )}
        </div>
      </div>

      {/* Affiliations row */}
      {affiliations.length > 0 && (
        <div className="authors-panel__section">
          <Typography className="authors-panel__label">Affiliations</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {affiliations.map((aff, i) => (
              <Chip
                key={i}
                size="small"
                style={{ borderRadius: 6 }}
                label={
                  <span>
                    <sup>{i + 1}</sup> {aff.institution}{aff.country ? `, ${aff.country}` : ""}
                  </span>
                }
                onDelete={readOnly ? undefined : () => deleteAffiliation(i)}
                deleteIcon={<CloseIcon />}
              />
            ))}
          </Stack>
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{editingIndex !== null ? "Edit author" : "Add author"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Given name"
            type="text"
            fullWidth
            size="small"
            value={draft.givenName ?? ""}
            onChange={(e) => setDraft((d) => ({ ...d, givenName: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Family name"
            type="text"
            fullWidth
            size="small"
            value={draft.familyName ?? ""}
            onChange={(e) => setDraft((d) => ({ ...d, familyName: e.target.value }))}
          />

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!draft.presenting}
                onChange={() => setDraft((d) => ({ ...d, presenting: !d.presenting }))}
              />
            }
            label="Presenting author"
            sx={{ mt: 0.5 }}
          />

          {/* Existing affiliations */}
          {affiliations.length > 0 && (
            <div className="authors-panel__dialog-affs">
              <Typography variant="caption" className="authors-panel__dialog-affs-label">
                Affiliations
              </Typography>
              {affiliations.map((aff, i) => (
                <FormControlLabel
                  key={i}
                  control={
                    <Checkbox
                      size="small"
                      checked={(draft.affiliations ?? []).includes(i)}
                      onChange={() => toggleDraftAff(i)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      <sup>{i + 1}</sup> {aff.institution}{aff.country ? `, ${aff.country}` : ""}
                    </Typography>
                  }
                />
              ))}
            </div>
          )}

          {/* Add new affiliation inline */}
          {showNewAff ? (
            <div className="authors-panel__dialog-new-aff">
              <Typography variant="caption" className="authors-panel__dialog-affs-label">
                New affiliation
              </Typography>
              <TextField
                margin="dense"
                label="Institution"
                type="text"
                fullWidth
                size="small"
                value={newAff.institution}
                onChange={(e) => setNewAff((a) => ({ ...a, institution: e.target.value }))}
              />
              <TextField
                margin="dense"
                label="Country"
                type="text"
                fullWidth
                size="small"
                value={newAff.country}
                onChange={(e) => setNewAff((a) => ({ ...a, country: e.target.value }))}
              />
            </div>
          ) : (
            <Button
              size="small"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setShowNewAff(true)}
              sx={{ mt: 1 }}
            >
              Add new affiliation
            </Button>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDialog} size="small">Cancel</Button>
          <Button onClick={saveDialog} size="small" variant="contained" disabled={!canSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
